/**
 * Hub Access Definitions
 *
 * This module defines which actions grant access to which hubs.
 * Hub visibility is derived from having ANY of the associated entity actions.
 *
 * Pattern: If a user can perform ANY action on entities managed in a hub,
 * they should be able to see that hub.
 */

/**
 * Actions that grant access to the Admin Hub.
 * Having any of these actions means the user should see the Admin Hub.
 */
export const ADMIN_HUB_ACTIONS = [
  // User management
  'user:admin:view',
  'user:admin:edit',
  'user:roles:assign',
  'user:memo:view',
  'user:memo:edit',
  // Role management
  'role:list',
  'role:view',
  'role:create',
  'role:edit',
  'role:delete',
  'role:actions:assign',
  // Franchise management
  'franchise:create',
  'franchise:edit',
  'franchise:delete',
  // Season management
  'season:create',
  'season:edit',
  // Settings
  'settings:view',
  'settings:edit',
  // Session management
  'session:list',
  'session:revoke',
  // Audit
  'audit:view',
  // Transaction approval/execution
  'transaction:approve',
  'transaction:execute',
  // Combine admin
  'combine:admin',
  // Staff
  'staff:create',
] as const;

/**
 * Actions that grant access to the GM Hub.
 * Having any of these actions means the user should see the GM Hub.
 */
export const GM_HUB_ACTIONS = [
  // Team management
  'team:create',
  'team:edit',
  'team:delete',
  // Franchise management (GM manages their franchise)
  'franchise:edit',
  // Transaction management
  'transaction:create',
  'transaction:list',
  'transaction:view',
] as const;

/**
 * Actions that grant access to the TM (Team Manager) Hub.
 * Having any of these actions means the user should see the TM Hub.
 */
export const TM_HUB_ACTIONS = [
  // Team management
  'team:edit',
  // Transaction viewing
  'transaction:list',
  'transaction:view',
] as const;

/**
 * Actions that grant access to the Transaction Manager Hub.
 * This is for league staff who approve/execute transactions.
 */
export const TRANSACTION_MANAGER_HUB_ACTIONS = [
  'transaction:list',
  'transaction:view',
  'transaction:approve',
  'transaction:execute',
] as const;

/**
 * Actions that grant access to the Tech Hub.
 * Having any of these actions means the user should see the Tech Hub.
 */
export const TECH_HUB_ACTIONS = [
  // Settings management
  'settings:view',
  'settings:edit',
  // Session management
  'session:list',
  'session:revoke',
  // Audit viewing
  'audit:view',
] as const;

/**
 * Actions that grant access to specific AdminHub cards.
 * Used to show/hide individual management cards within the Admin Hub.
 */
export const ADMIN_HUB_CARD_ACTIONS = {
  userManagement: [
    'user:admin:view',
    'user:admin:edit',
    'user:roles:assign',
    'user:memo:view',
    'user:memo:edit',
  ],
  roleManagement: [
    'role:list',
    'role:view',
    'role:create',
    'role:edit',
    'role:delete',
    'role:actions:assign',
  ],
  franchiseManagement: [
    'franchise:list',
    'franchise:view',
    'franchise:create',
    'franchise:edit',
    'franchise:delete',
  ],
  seasonManagement: [
    'season:list',
    'season:view',
    'season:create',
    'season:edit',
  ],
  teamManagement: [
    'team:list',
    'team:view',
    'team:create',
    'team:edit',
    'team:delete',
  ],
  settings: [
    'settings:view',
    'settings:edit',
  ],
  sessions: [
    'session:list',
    'session:revoke',
  ],
  audit: [
    'audit:view',
  ],
  transactions: [
    'transaction:list',
    'transaction:view',
    'transaction:approve',
    'transaction:execute',
  ],
} as const;

/**
 * Type helper for action arrays
 */
export type HubActions = readonly string[];

/**
 * Helper to get all actions for a hub
 */
export function getHubActions(hub: 'admin' | 'gm' | 'tm' | 'tech' | 'transaction-manager'): readonly string[] {
  switch (hub) {
    case 'admin':
      return ADMIN_HUB_ACTIONS;
    case 'gm':
      return GM_HUB_ACTIONS;
    case 'tm':
      return TM_HUB_ACTIONS;
    case 'tech':
      return TECH_HUB_ACTIONS;
    case 'transaction-manager':
      return TRANSACTION_MANAGER_HUB_ACTIONS;
    default:
      return [];
  }
}
